import {
  useMutationXeroUpdateAccountMapping,
  useQueryXeroGetAccountMapping,
  useQueryXeroGetAccountMappingOptionList,
} from "@gymflow/api";
import {
  Button,
  FormInput,
  FormSelectInput,
  Spinner,
} from "apps/portal/src/components/atoms";
import { SettingsRoute } from "apps/portal/src/components/Settings/SettingsRoute";
import { usePortalRoutes } from "apps/portal/src/hooks";
import { useClubSettings } from "apps/portal/src/providers";
import { ToastContext } from "apps/portal/src/providers/ToastProvider/context";
import { RouteFeature, RouteLayout } from "apps/portal/src/routes";
import useGymflowModels from "apps/portal/src/store";
import { isAxiosError } from "axios";
import { FormikProvider, useFormik } from "formik";
import { useContext, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import { toFormikValidationSchema } from "zod-formik-adapter";

import {
  INITIAL_VALUES_XERO_INTEGRATION_FORM,
  xeroIntegrationFormSchema,
} from "./constants";
import { XeroIntegrationFormProps, XeroIntegrationFormValues } from "./types";
import { mapToSelectOptions } from "./utils/mapToSelectOptions";
import { populateFormFromAccountMapping } from "./utils/populateFormFromAccountMapping";

export const XeroIntegrationForm = ({
  isOnboarding,
}: XeroIntegrationFormProps) => {
  const isInitialized = useRef(false);

  const { t } = useTranslation();
  const { api } = useGymflowModels();
  const { clubId } = useClubSettings();
  const { notifyDanger, toast } = useContext(ToastContext);
  const { push } = useHistory();
  const { createClubLink } = usePortalRoutes();

  const { data: dataAccountMapping, isFetching: isFetchingAccountMapping } =
    useQueryXeroGetAccountMapping({ api });

  const {
    data: dataAccountMappingOptionList,
    isFetching: isFetchingAccountMappingOptionList,
  } = useQueryXeroGetAccountMappingOptionList({ api });

  const isFetching =
    isFetchingAccountMapping || isFetchingAccountMappingOptionList;

  const { mutateAsync: mutateAsyncUpdateAccountMapping } =
    useMutationXeroUpdateAccountMapping({ api, clubId });

  const formik = useFormik<XeroIntegrationFormValues>({
    initialValues: INITIAL_VALUES_XERO_INTEGRATION_FORM,
    validationSchema: toFormikValidationSchema(xeroIntegrationFormSchema),
    onSubmit: async ({ adjustment, charge, fee, other, payout, refund }) => {
      try {
        await mutateAsyncUpdateAccountMapping({
          adjustmentAccountId: adjustment?.value ?? "",
          chargeAccountId: charge?.value ?? "",
          feeAccountId: fee?.value ?? "",
          otherAccountId: other?.value ?? "",
          payoutAccountId: payout?.value ?? "",
          refundAccountId: refund?.value ?? "",
        });
        push(
          createClubLink(
            RouteLayout.Staff,
            RouteFeature.Settings,
            SettingsRoute.Integrations,
          ),
        );

        toast({
          type: "small",
          intent: "success",
          message: isOnboarding
            ? t(
                "page.settings.tab.integrations.xeroIntegration.form.toast.xeroIntegrated",
              )
            : t(
                "page.settings.tab.integrations.xeroIntegration.form.toast.xeroUpdated",
              ),
        });
      } catch (e) {
        if (isAxiosError(e)) {
          notifyDanger(e);
        }
      }
    },
  });

  const { resetForm } = formik;

  useEffect(() => {
    return () => {
      resetForm();
      isInitialized.current = false;
    };
  }, [resetForm]);

  useEffect(() => {
    if (!isInitialized.current && dataAccountMapping && !isFetching) {
      populateFormFromAccountMapping(formik, dataAccountMapping);
      isInitialized.current = true;
    }
  }, [dataAccountMapping, formik, isFetching]);

  const selectOptions = useMemo(
    () => ({
      payout: mapToSelectOptions(dataAccountMappingOptionList?.payout),
      charge: mapToSelectOptions(dataAccountMappingOptionList?.charge),
      refund: mapToSelectOptions(dataAccountMappingOptionList?.refund),
      fee: mapToSelectOptions(dataAccountMappingOptionList?.fee),
      adjustment: mapToSelectOptions(dataAccountMappingOptionList?.adjustment),
      other: mapToSelectOptions(dataAccountMappingOptionList?.other),
    }),
    [dataAccountMappingOptionList],
  );

  return (
    <FormikProvider value={formik}>
      {isFetching ? (
        <Spinner />
      ) : (
        <div className="flex size-full max-h-full flex-col overflow-y-hidden rounded-2xl border border-gray-200">
          <div className="flex flex-1 flex-col gap-8 overflow-y-auto p-6">
            <div className="flex flex-col gap-4">
              <div className="text-base font-semibold text-gray-950">
                {t(
                  "page.settings.tab.integrations.xeroIntegration.form.payoutsAndTransfers",
                )}
              </div>
              <div className="flex flex-col gap-4 lg:flex-row">
                <FormInput<XeroIntegrationFormValues>
                  name="clearing"
                  disabled
                  containerClassName="w-full"
                  isRequired
                  label={t(
                    "page.settings.tab.integrations.xeroIntegration.form.clearingAccount",
                  )}
                />
                <FormSelectInput<XeroIntegrationFormValues, string>
                  name="payout"
                  label={t(
                    "page.settings.tab.integrations.xeroIntegration.form.payoutBankAccount",
                  )}
                  containerClassName="w-full"
                  isRequired
                  options={selectOptions.payout}
                />
              </div>
            </div>
            <div className="flex flex-col gap-4 border-t border-t-gray-300 pt-8">
              <div className="text-base font-semibold text-gray-950">
                {t(
                  "page.settings.tab.integrations.xeroIntegration.form.transactionReconciliation",
                )}
              </div>
              <div className="flex flex-col gap-4 lg:flex-row">
                <FormSelectInput<XeroIntegrationFormValues, string>
                  name="charge"
                  label={t(
                    "page.settings.tab.integrations.xeroIntegration.form.charges",
                  )}
                  containerClassName="w-full"
                  isRequired
                  options={selectOptions.charge}
                />
                <FormSelectInput<XeroIntegrationFormValues, string>
                  name="refund"
                  label={t(
                    "page.settings.tab.integrations.xeroIntegration.form.refunds",
                  )}
                  containerClassName="w-full"
                  isRequired
                  options={selectOptions.refund}
                />
              </div>
              <div className="flex flex-col gap-4 lg:flex-row">
                <FormSelectInput<XeroIntegrationFormValues, string>
                  name="fee"
                  label={t(
                    "page.settings.tab.integrations.xeroIntegration.form.processingFees",
                  )}
                  containerClassName="w-full"
                  isRequired
                  options={selectOptions.fee}
                />
                <FormSelectInput<XeroIntegrationFormValues, string>
                  name="adjustment"
                  label={t(
                    "page.settings.tab.integrations.xeroIntegration.form.adjustments",
                  )}
                  containerClassName="w-full"
                  isRequired
                  options={selectOptions.adjustment}
                />
              </div>
              <div className="flex flex-col gap-4 lg:flex-row">
                <FormSelectInput<XeroIntegrationFormValues, string>
                  name="other"
                  label={t(
                    "page.settings.tab.integrations.xeroIntegration.form.other",
                  )}
                  containerClassName="w-full"
                  isRequired
                  options={selectOptions.other}
                />
                <div className="w-full" />
              </div>
            </div>
          </div>
          <div className="flex flex-col items-end border-t border-t-gray-200 px-6 py-4">
            <Button
              intent="secondary"
              onClick={async () => await formik.submitForm()}
            >
              {t("common.save")}
            </Button>
          </div>
        </div>
      )}
    </FormikProvider>
  );
};
